import { toast } from 'sonner';
import { getSupabaseClient } from '../supabaseClient';

/**
 * Fetches all available connections from the database
 * @returns {Promise<Array>} Array of connection objects
 */
export const fetchConnections = async () => {
  try {
    const supabase = getSupabaseClient();

    // Query the connections table from Supabase
    const { data, error } = await supabase
      .from('connections')
      .select('*')
      .order('name');

    if (error) {
      throw error;
    }

    // Map database fields to the format expected by the UI
    const connections = data.map(conn => ({
      id: conn.id,
      name: conn.name,
      type: conn.type || 'custom', // Default to custom if type is not set
      image: conn.image,
      created_at: conn.created_at
    }));

    return connections;
  } catch (error) {
    console.error('Error fetching connections:', error);
    toast.error('Failed to load connections');
    throw error;
  }
};

/**
 * Fetches actions for a specific connection
 * @param {string} connectionId The ID of the connection
 * @returns {Promise<Array>} Array of action objects
 */
export const fetchConnectionActions = async (connectionId) => {
  try {
    const supabase = getSupabaseClient();

    // Query the connections_actions table from Supabase (corrected table name)
    const { data, error } = await supabase
      .from('connections_actions')
      .select('*')
      .eq('connection_id', connectionId)
      .order('name');

    if (error) {
      throw error;
    }

    // If no actions exist yet in the database, return placeholders based on connection type
    if (!data || data.length === 0) {
      // Fetch connection details to determine type
      const { data: connData, error: connError } = await supabase
        .from('connections')
        .select('name, type')
        .eq('id', connectionId)
        .single();

      if (connError) throw connError;

      // Determine connection type
      const connType = (connData.type || connData.name || '').toLowerCase();

      // Return default actions based on type
      if (connType.includes('curbside')) {
        return [
          { id: 'place-order', name: 'Place New Order', description: 'Create a new customer order', category: 'orders', connection_id: connectionId },
          { id: 'cancel-order', name: 'Cancel Order', description: 'Cancel an existing order', category: 'orders', connection_id: connectionId }
        ];
      } else if (connType.includes('cleancloud')) {
        return [
          { id: 'place-order', name: 'Create Order', description: 'Create a new CleanCloud order', category: 'orders', connection_id: connectionId },
          { id: 'schedule-pickup', name: 'Schedule Pickup', description: 'Schedule a pickup for customer', category: 'logistics', connection_id: connectionId }
        ];
      } else {
        return [
          { id: 'custom-action', name: 'Custom Action', description: 'Use custom API endpoint', category: 'custom', connection_id: connectionId }
        ];
      }
    }

    return data;
  } catch (error) {
    console.error('Error fetching connection actions:', error);
    toast.error('Failed to load connection actions');
    throw error;
  }
};

/**
 * Creates a new connection configuration
 * @param {Object} connectionData The connection data to create
 * @returns {Promise<Object>} The created connection object
 */
export const createConnection = async (connectionData) => {
  try {
    const supabase = getSupabaseClient();

    // Insert the new connection into the database
    const { data, error } = await supabase
      .from('connections')
      .insert({
        name: connectionData.name,
        type: connectionData.type,
        image: connectionData.image || '/logos/api.svg',
        endpoint: connectionData.endpoint,
        api_key: connectionData.apiKey
      })
      .select()
      .single();

    if (error) throw error;

    toast.success('Connection created successfully');

    // Return the newly created connection
    return {
      id: data.id,
      name: data.name,
      type: data.type || 'custom',
      endpoint: data.endpoint,
      image: data.image
    };
  } catch (error) {
    console.error('Error creating connection:', error);
    toast.error('Failed to create connection');
    throw error;
  }
};

/**
 * Gets presets for a specific connection type and action
 * @param {string} connectionType The type of connection
 * @param {string} actionId The ID of the action
 * @returns {Object} Object containing preset questions and parameters
 */
export const getConnectionPresets = (connectionType, actionId) => {
  // This would typically fetch from a database or API
  // For now, we'll just return hardcoded presets

  // Preset questions for different connection types and actions
  const presetQuestions = {
    curbside: {
      'place-order': [
        {
          type: 'text',
          text: 'Customer Phone Number',
          required: true,
        },
        {
          type: 'select',
          text: 'Service Type',
          required: true,
          options: ['Wash & Fold', 'Dry Cleaning', 'Both']
        },
        {
          type: 'date',
          text: 'Pickup Date',
          required: true,
        },
        {
          type: 'select',
          text: 'Pickup Time Window',
          required: true,
          options: ['8am-12pm', '12pm-4pm', '4pm-8pm']
        },
        {
          type: 'date',
          text: 'Delivery Date',
          required: true,
        },
        {
          type: 'select',
          text: 'Delivery Time Window',
          required: true,
          options: ['8am-12pm', '12pm-4pm', '4pm-8pm']
        },
        {
          type: 'textarea',
          text: 'Special Instructions',
          required: false,
        }
      ],
      'cancel-order': [
        {
          type: 'text',
          text: 'Order Number',
          required: true,
        },
        {
          type: 'textarea',
          text: 'Cancellation Reason',
          required: false,
        }
      ]
    },
    cleancloud: {
      'place-order': [
        {
          type: 'text',
          text: 'Customer ID',
          required: true,
        },
        {
          type: 'select',
          text: 'Service Type',
          required: true,
          options: ['Wash & Press', 'Dry Clean', 'Alterations']
        },
        {
          type: 'date',
          text: 'Pickup Date',
          required: true,
        }
      ]
    }
  };

  // Preset parameters for different connection types and actions
  const presetParameters = {
    curbside: {
      'place-order': [
        { param: 'accountId', questionId: 'fixed', value: 'acc123' },
        { param: 'pickup_date', questionId: 'order_2' },
        { param: 'pickup_time', questionId: 'order_3' },
        { param: 'delivery_date', questionId: 'order_4' },
        { param: 'delivery_time', questionId: 'order_5' },
        { param: 'services', questionId: 'order_1' },
        { param: 'instructions', questionId: 'order_6' },
        { param: 'curbsideAccountPhone', questionId: 'order_0' },
      ],
      'cancel-order': [
        { param: 'accountId', questionId: 'fixed', value: 'acc123' },
        { param: 'orderId', questionId: 'order_0' },
        { param: 'reason', questionId: 'order_1' },
      ]
    },
    cleancloud: {
      'place-order': [
        { param: 'customer_id', questionId: 'order_0' },
        { param: 'service_type', questionId: 'order_1' },
        { param: 'pickup_date', questionId: 'order_2' },
      ]
    }
  };

  return {
    questions: presetQuestions[connectionType]?.[actionId] || [],
    parameters: presetParameters[connectionType]?.[actionId] || []
  };
};

/**
 * Checks if a connection is configured for the current account
 * @param {string} connectionId The ID of the connection
 * @returns {Promise<Object>} The configuration object if it exists
 */
export const checkConnectionConfig = async (connectionId) => {
  try {
    const supabase = getSupabaseClient();

    // Get current user ID from auth - this is the same as the account ID
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.log('User not authenticated when checking connection config');
      return null;
    }

    // The account ID is the same as the user ID based on the schema
    const accountId = user.id;

    console.log(`Checking config for connection ID: ${connectionId} and account ID: ${accountId}`);

    // Check if a configuration exists for this connection and account
    const { data, error } = await supabase
      .from('connections_config')
      .select('*')
      .eq('connection_id', connectionId)
      .eq('account_id', accountId)
      .single();

    if (error) {
      // PGRST116 is "not found" which we expect when there's no config
      if (error.code === 'PGRST116') {
        console.log('No configuration found for this connection (expected)');
      } else {
        console.error('Error checking connection config:', error);
        // For 406 Not Acceptable errors, typically a formatting issue with the request
        if (error.status === 406) {
          console.log('Received 406 Not Acceptable - likely an issue with content type negotiation');
        }
      }
      return null;
    }

    console.log('Configuration found:', data);
    return data;
  } catch (error) {
    console.error('Error checking connection config:', error);
    return null;
  }
};

export const fetchConnectionActionFields = async (connectionActionId) => {
  try {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('connections_actions_fields')
      .select('*')
      .eq('connection_action_id', connectionActionId)
      .order('order', { ascending: true });

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Error fetching action fields:', err);
    toast.error('Failed to load questions for action');
    return [];
  }
}; 